import { latoChePensa, verdettoOrologio } from '@/lib/qgammon/orologio';
import { normalizzaPercorso } from '@/components/layout/tabBarRoutes';

/**
 * LA CONFERMA PRIMA DI LASCIARE UN TAVOLO VIVO — quando serve davvero.
 *
 * [Luca, I-392, ~23:39Z, «4 (b)», via tma-orcb] Con la barra sul tavolo (I-383) un tocco su una voce lo lascia, e a metà
 * del PROPRIO turno l'orologio del server continua a correre: a zero si perde per tempo, e la pastiglia che riporta al
 * tavolo non dice quanto ne resta. Prima della barra l'unico modo di uscire era il tasto indietro di Telegram, che non
 * chiede niente (`useBackButton.ts`): dal 2026-10-05 (I-402, Luca) la conferma copre anche quello, con lo stesso predicato
 * (`contexts/UscitaTavoloContext.tsx`).
 *
 * ═══ QUANDO ═══
 * Quando scorre il MIO tempo (`latoChePensa(state) === perspective`) **in una partita con orologio**
 * (`verdettoOrologio(...).tipo === 'orologio'`). Un solo predicato, e tutti i casi in cui la conferma sarebbe inutile
 * escono da sé perché `latoChePensa` dà `null` o l'altro lato:
 *  · il turno dell'avversario (il suo orologio scorre, il mio è fermo: lasciare costa zero);
 *  · l'appuntamento (`status: 'waiting'`: nessun orologio), fra una partita e l'altra (`betweenGames`), a partita finita
 *    (`gameOver`), la pausa concordata (orologi fermi);
 *  · senza orologio (`unlimited`): `'senza'`, e `'ignoto'` se il blocco manca (non si chiede a vuoto su ciò che non si sa);
 *  · il cubo offerto: a decidere è il RICEVENTE e il suo orologio scorre (`latoChePensa` lo sa), quindi chiede a lui e non
 *    a chi ha offerto.
 *
 * ⚠️ Solo il BACKGAMMON: la proiezione di QChinesePoker non porta un orologio (`projectQcp`) e non so se il gameserver ne
 * abbia uno; chi chiama controlla `descriptor.game === 'qgammon'` PRIMA di arrivare qui.
 * ⚠️ LIMITE, dichiarato: il filo non porta ne' `running` ne' la presenza. Con l'AVVERSARIO caduto l'orologio sul server è
 * in pausa e noi non lo vediamo: la conferma è allora inutile (per ECCESSO, una domanda in più).
 * ⚠️ E si può sbagliare anche per DIFETTO: è una RETE, non una garanzia (ADR-0196 §2(d)). Con uno stato in ritardo (latenza,
 * riconnessione) `qgammon.state` può non dire ancora che il turno è passato a me, e la conferma può mancare con l'orologio vivo.
 *
 * `istanti` serve solo all'estrapolazione del tempo, che qui non interessa: per decidere basta il TIPO del verdetto.
 */
export function uscitaCostaOrologio(
  state: unknown,
  perspective: 'W' | 'B' | null | undefined,
  adesso: number = Date.now(),
): boolean {
  if (perspective !== 'W' && perspective !== 'B') return false;
  if (latoChePensa(state) !== perspective) return false;
  return verdettoOrologio(state, { adesso, ricevutoA: adesso }).tipo === 'orologio';
}

/**
 * Vero se `pathname` e' il tavolo di QUELLA partita (`/backgammon/match/<matchId>`), normalizzato: slash finale e maiuscole
 * come in tutta la barra. Il tavolo di un'ALTRA partita, il replay e lo spettatore NON sono «il tavolo»: da li' un tocco sulla
 * barra non lascia nessun orologio vivo.
 */
export function eIlTavolo(pathname: string, matchId: string): boolean {
  return normalizzaPercorso(pathname) === `/backgammon/match/${matchId}`.toLowerCase();
}
